import { useEffect, useSyncExternalStore } from 'react';

/**
 * 動的なページタイトル (商品名・ユーザー名など、ルート定義には書けない値) を
 * DocumentTitleSync に渡すための小さな外部ストア。
 *
 * なぜ document.title を直接書かないか:
 *   子コンポーネントの effect は親より先に走るため、ページ側が直接書くと
 *   ルート側の解決 (DocumentTitleSync) に上書き負けする。解決を 1 箇所に集約し、
 *   ページはここへ「登録」するだけにすることで順序依存を無くす。
 */

export interface TitleOverride {
  /** ページ名。null ならサフィックスのみを表示する */
  title: string | null;
  /** サフィックスの上書き。未指定ならルートの titleSuffix / serviceName を使う */
  suffix?: string;
}

let current: TitleOverride | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return current;
}

/** DocumentTitleSync 専用。現在の override を購読する */
export function useTitleOverride(): TitleOverride | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * ページ側から動的タイトルを登録する。
 *
 * @param title  ページ名。null / undefined を渡すと登録しない (ルート定義の値が使われる)
 * @param options.suffix サフィックスを上書きしたい場合に指定 (例: マニュアル)
 *
 * データ取得中は undefined を渡しておけば、確定後に自動で差し替わる。
 */
export function useDocumentTitle(
  title: string | null | undefined,
  options?: { suffix?: string },
): void {
  const suffix = options?.suffix;
  useEffect(() => {
    // title も suffix も無いなら登録不要 (ルート定義の解決に任せる)
    if (title === undefined && suffix === undefined) return;
    const entry: TitleOverride = { title: title ?? null, suffix };
    current = entry;
    emit();
    return () => {
      // 別ページが既に自分の override を登録済みなら、それを消さない。
      // (unmount → mount の順序は React が保証するため通常は自分自身)
      if (current === entry) {
        current = null;
        emit();
      }
    };
  }, [title, suffix]);
}
